if (!customElements.get('pinch-zoom')) {
  customElements.define(
    'pinch-zoom',
    class PinchZoom extends HTMLElement {
      constructor() {
        super();
        this.scale = 1;
        this.translateX = 0;
        this.translateY = 0;
        this.minimumScale = 1;
        this.maximumScale = 4;

        this.onTouchStart = this.onTouchStart.bind(this);
        this.onTouchMove = this.onTouchMove.bind(this);
        this.onTouchEnd = this.onTouchEnd.bind(this);
        this.reset = this.reset.bind(this);
      }

      connectedCallback() {
        this.image = this.querySelector('.product-media-modal__pinch-image');
        if (!this.image) return;

        this.addEventListener('touchstart', this.onTouchStart, { passive: false });
        this.addEventListener('touchmove', this.onTouchMove, { passive: false });
        this.addEventListener('touchend', this.onTouchEnd, { passive: false });
        this.addEventListener('touchcancel', this.onTouchEnd, { passive: false });
        document.body.addEventListener('modalClosed', this.reset);
      }

      disconnectedCallback() {
        this.removeEventListener('touchstart', this.onTouchStart);
        this.removeEventListener('touchmove', this.onTouchMove);
        this.removeEventListener('touchend', this.onTouchEnd);
        this.removeEventListener('touchcancel', this.onTouchEnd);
        document.body.removeEventListener('modalClosed', this.reset);
      }

      onTouchStart(event) {
        if (event.touches.length === 2) {
          event.preventDefault();
          const midpoint = this.getMidpoint(event.touches[0], event.touches[1]);
          const base = this.getBasePosition();

          this.gesture = {
            type: 'pinch',
            distance: this.getDistance(event.touches[0], event.touches[1]),
            scale: this.scale,
            contentX: (midpoint.x - base.x - this.translateX) / this.scale,
            contentY: (midpoint.y - base.y - this.translateY) / this.scale,
          };
          return;
        }

        if (event.touches.length === 1 && this.scale <= this.minimumScale && window.matchMedia('(max-width: 749px)').matches) {
          const touch = event.touches[0];
          this.gesture = {
            type: 'swipe',
            clientX: touch.clientX,
            clientY: touch.clientY,
          };
          return;
        }

        if (event.touches.length === 1 && this.scale > this.minimumScale) {
          const touch = event.touches[0];
          this.gesture = {
            type: 'pan',
            clientX: touch.clientX,
            clientY: touch.clientY,
            translateX: this.translateX,
            translateY: this.translateY,
          };
        }
      }

      onTouchMove(event) {
        if (event.touches.length === 2) {
          event.preventDefault();

          if (!this.gesture || this.gesture.type !== 'pinch') {
            this.onTouchStart(event);
            return;
          }

          const midpoint = this.getMidpoint(event.touches[0], event.touches[1]);
          const distance = this.getDistance(event.touches[0], event.touches[1]);
          const nextScale = this.clamp(
            this.gesture.scale * (distance / this.gesture.distance),
            this.minimumScale,
            this.maximumScale
          );
          const base = this.getBasePosition();

          this.scale = nextScale;
          this.translateX = midpoint.x - base.x - this.gesture.contentX * nextScale;
          this.translateY = midpoint.y - base.y - this.gesture.contentY * nextScale;
          this.clampTranslation();
          this.render();
          return;
        }

        if (event.touches.length === 1 && this.gesture?.type === 'swipe') {
          const touch = event.touches[0];
          if (Math.abs(touch.clientX - this.gesture.clientX) > Math.abs(touch.clientY - this.gesture.clientY)) {
            event.preventDefault();
          }
          return;
        }

        if (event.touches.length === 1 && this.scale > this.minimumScale) {
          event.preventDefault();
          const touch = event.touches[0];

          if (!this.gesture || this.gesture.type !== 'pan') {
            this.onTouchStart(event);
            return;
          }

          this.translateX = this.gesture.translateX + touch.clientX - this.gesture.clientX;
          this.translateY = this.gesture.translateY + touch.clientY - this.gesture.clientY;
          this.clampTranslation();
          this.render();
        }
      }

      onTouchEnd(event) {
        if (event.touches.length === 0 && this.gesture?.type === 'swipe' && event.type === 'touchend') {
          const touch = event.changedTouches[0];
          const deltaX = touch.clientX - this.gesture.clientX;
          const deltaY = touch.clientY - this.gesture.clientY;
          const swipeThreshold = Math.max(45, this.clientWidth * 0.12);

          this.gesture = null;
          if (Math.abs(deltaX) >= swipeThreshold && Math.abs(deltaX) > Math.abs(deltaY) * 1.25) {
            this.showSiblingImage(deltaX < 0 ? 1 : -1);
          }
          return;
        }

        if (event.touches.length === 1 && this.scale > this.minimumScale) {
          const touch = event.touches[0];
          this.gesture = {
            type: 'pan',
            clientX: touch.clientX,
            clientY: touch.clientY,
            translateX: this.translateX,
            translateY: this.translateY,
          };
          return;
        }

        this.gesture = null;
        if (this.scale <= this.minimumScale + 0.01) this.reset();
      }

      showSiblingImage(direction) {
        const modal = this.closest('product-modal');
        if (!modal) return;

        const images = Array.from(
          modal.querySelectorAll('.product-media-modal__content > pinch-zoom[data-media-id]')
        );
        if (images.length < 2) return;

        const currentImage = modal.querySelector(
          '.product-media-modal__content > pinch-zoom[data-media-id].active'
        );
        const currentIndex = images.indexOf(currentImage || this);
        const nextIndex = (currentIndex + direction + images.length) % images.length;
        const nextImage = images[nextIndex];

        images.forEach((image) => {
          image.classList.remove('active');
          image.reset?.();
        });
        nextImage.classList.add('active');
      }

      getMidpoint(firstTouch, secondTouch) {
        const bounds = this.getBoundingClientRect();
        return {
          x: (firstTouch.clientX + secondTouch.clientX) / 2 - bounds.left,
          y: (firstTouch.clientY + secondTouch.clientY) / 2 - bounds.top,
        };
      }

      getDistance(firstTouch, secondTouch) {
        return Math.hypot(secondTouch.clientX - firstTouch.clientX, secondTouch.clientY - firstTouch.clientY);
      }

      getBasePosition() {
        return {
          x: (this.clientWidth - this.image.offsetWidth) / 2,
          y: (this.clientHeight - this.image.offsetHeight) / 2,
        };
      }

      clampTranslation() {
        const base = this.getBasePosition();
        const scaledWidth = this.image.offsetWidth * this.scale;
        const scaledHeight = this.image.offsetHeight * this.scale;
        const horizontalAllowance = Math.min(this.clientWidth * 0.15, scaledWidth * 0.1);
        const verticalAllowance = Math.min(this.clientHeight * 0.25, scaledHeight * 0.15);

        if (scaledWidth <= this.clientWidth) {
          this.translateX = (this.clientWidth - scaledWidth) / 2 - base.x;
        } else {
          this.translateX = this.clamp(
            this.translateX,
            this.clientWidth - base.x - scaledWidth - horizontalAllowance,
            -base.x + horizontalAllowance
          );
        }

        if (scaledHeight <= this.clientHeight) {
          this.translateY = (this.clientHeight - scaledHeight) / 2 - base.y;
        } else {
          this.translateY = this.clamp(
            this.translateY,
            this.clientHeight - base.y - scaledHeight - verticalAllowance,
            -base.y + verticalAllowance
          );
        }
      }

      clamp(value, minimum, maximum) {
        return Math.min(maximum, Math.max(minimum, value));
      }

      render() {
        this.image.style.transform = `translate3d(${this.translateX}px, ${this.translateY}px, 0) scale(${this.scale})`;
      }

      reset() {
        if (!this.image) return;
        this.gesture = null;
        this.scale = this.minimumScale;
        this.translateX = 0;
        this.translateY = 0;
        this.image.style.transition = 'transform 180ms ease';
        this.render();
        window.setTimeout(() => {
          if (this.image) this.image.style.transition = '';
        }, 180);
      }
    }
  );
}
